import { Injectable } from '@angular/core';
import { getAuth } from '@angular/fire/auth';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { createStore, select, withProps } from '@ngneat/elf';
import { TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getErrorData } from '../common/utils';
import {
  FirebaseCollections,
} from '../consts/firebase.consts';
import { AppRoutePaths } from '../consts/routes.consts';
import { CheckInProcessSteps } from '../enums/check-in-process-steps.enum';
import { IEarlyCheckInDataModel } from '../models/early-check-in-data.model';
import {
  KioskCarsAvailabilityModel,
  KioskProductModel,
} from '../models/kiosk-cars-availability-response.model';
import { KioskReservationModel } from '../models/kiosk-get-reservation-response.model';
import { ISetDriverDeatilsModel } from '../models/set-driver-details.model';
import { ClientService } from '../services/client.service';
import { FilesService } from '../services/file.service';
import { ExpirationBoundEntityRepository } from './expiration-bound-entity.repository';

interface ReservationProps {
  reservation: any;
  ui: {
    expirationBoundEntityId?: string;
    processId?: string;
    googleWalletLink?: string;
    scanDriverId?: string;
    reservationNumber: any;
    tafnitDriver: any;
    reservationInputNumber: any;
    lastName: string;
    qrUrl: string;
    processStep: string;
    lang: string;
    branchCode: any;
    driverDetails: any;
    selectedCar: KioskCarsAvailabilityModel;
    selectedCovers: KioskProductModel[];
    selectedAddOns: KioskProductModel[];
    reservationsList: any[];
    isIsraeliLicense: boolean;
    isLoading: boolean;
    displayDeskBtn: boolean;
    isInExtraDriverScan: boolean;
    contractPdfLink: string;
    passportImg?: string;
    extraDriverLicenseImg?: string;
    licenseImg?: string;
    extraFileName?: string;
    passportFileName?: string;
    mainFileName?: string;
    reservationFromFb: any;
  };
}

const reservationStore = createStore(
  { name: 'early-check-in-reservation' },
  withProps<ReservationProps>({
    reservation: new KioskReservationModel(),
    ui: {
      reservationNumber: '',
      expirationBoundEntityId: '',
      googleWalletLink: '',
      scanDriverId: undefined,
      tafnitDriver: undefined,
      reservationInputNumber: '',
      extraFileName: undefined,
      passportFileName: undefined,
      mainFileName: undefined,
      licenseImg: undefined,
      extraDriverLicenseImg: undefined,
      passportImg: undefined,
      processId: undefined,
      driverDetails: undefined,
      processStep: '',
      qrUrl: '',
      lastName: '',
      lang: 'en',
      branchCode: '',
      selectedCar: new KioskCarsAvailabilityModel(),
      selectedCovers: [],
      selectedAddOns: [],
      reservationsList: [],
      isIsraeliLicense: false,
      isLoading: false,
      displayDeskBtn: false,
      isInExtraDriverScan: false,
      contractPdfLink: '',
      reservationFromFb: undefined,
    },
  })
);

@Injectable({ providedIn: 'root' })
export class EarlyCheckInReservationRepository {
  constructor(
    private clientService: ClientService,
    private expirationBoundEntityRepository: ExpirationBoundEntityRepository,
    private firestore: Firestore,
    private filesService: FilesService,
    private ts: TranslocoService,
    private logger: NGXLogger,
    private router: Router
  ) {}

  public async getReservationFromFb(processId: string) {
    const docRef = doc(
      this.firestore,
      FirebaseCollections.EARLY_CHECK_IN_PROCESS,
      processId
    );
    const res = (await getDoc(docRef)).data();
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, reservationFromFb: res },
    }));
    return res;
  }

  public async updateCollection(data: any, processId?: string) {
    if (processId) {
      reservationStore.update((state) => ({
        ...state,
        ui: { ...state.ui, processId },
      }));
      localStorage.setItem('processId', processId);
    }

    const id = reservationStore.getValue().ui.processId || processId;
    if (id) {
      const docRef = doc(
        this.firestore,
        FirebaseCollections.EARLY_CHECK_IN_PROCESS,
        id
      );
      await setDoc(docRef, data, { merge: true });
    }
  }

  public resetStore() {
    reservationStore.reset();
    this.ts.setActiveLang('en');
  }

  public async getTafnitDriverDetails(legalId: string) {
    const state = this.getState();
    try {
      await this.setDriverDetails({ legalId }, true);

      const token = await getAuth().currentUser?.getIdToken();
      const processId = this.processId;
      const res = await lastValueFrom(
        this.clientService.getTafnitDriverEarlyCheckIn(
          {
            processId,
            userId: this.expirationBoundEntityId,
          },
          token
        )
      );
      reservationStore.update((state) => ({
        ...state,
        ui: { ...state.ui, tafnitDriver: res?.content || null },
      }));
      this.updateCollection({ tafnitDriver: res?.content || null });
    } catch (err) {
      this.setIsLoading(false);
      this.logger.error(
        `${state.reservationNumber} - ${state.processId} - getTafnitDriverDetails failed with res:`,
        { error: getErrorData(err) }
      );
    }
  }

  public setAddressNames(names: {
    matchingState?: string;
    matchingCountry?: string;
    matchingNationality?: string;
  }) {
    reservationStore.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        tafnitDriver: {
          ...state.ui.tafnitDriver,
          stateName: names.matchingState,
          countryName: names.matchingCountry,
          nationalityName: names.matchingNationality,
        },
      },
    }));
  }

  public getState() {
    return reservationStore.getValue().ui;
  }

  public reservation$ = reservationStore.pipe(
    select((state) => state.reservation)
  );

  public reservationNumber$ = reservationStore.pipe(
    select((state) => state.reservation?.reservationNumber)
  );

  public isLoading$ = reservationStore.pipe(
    select((state) => state.ui.isLoading)
  );

  public reservationFromFb$ = reservationStore.pipe(
    select((state) => state.ui.reservationFromFb)
  );

  public processId$ = reservationStore.pipe(
    select((state) => state.ui.processId)
  );

  public isHebrew$ = reservationStore.pipe(
    select((state) => state.ui.lang == 'he')
  );

  public earlyCheckInStepperStep$ = reservationStore.pipe(
    select((state) => {
      if (state.ui.processStep == CheckInProcessSteps.DriverDetails) {
        return 1;
      } else if (state.ui.processStep == CheckInProcessSteps.DriverLicense) {
        return 2;
      }
      if (state.ui.processStep == CheckInProcessSteps.DriverAddress) {
        return 3;
      } else {
        return 0;
      }
    })
  );

  public processStep$ = reservationStore.pipe(
    select((state) => state.ui.processStep)
  );

  public selectedLang$ = reservationStore.pipe(
    select((state) => state.ui.lang)
  );

  public isIsraeli$ = reservationStore.pipe(
    select((state) => state.ui.isIsraeliLicense)
  );

  public nationality$ = reservationStore.pipe(
    select((state) => state.ui.driverDetails?.nationality)
  );

  public async getKioskSettings() {
    const docRef = doc(
      this.firestore,
      FirebaseCollections.APP_SETTINGS,
      FirebaseCollections.KIOSK_SETTINGS
    );
    const res = (await getDoc(docRef)).data();
    return res;
  }

  public async earlyCheckInCompletedDetails(processId: string) {
    try {
      const existingFirebaseEntity = await this.getReservationFromFb(processId);
      if (existingFirebaseEntity) {
        const isHebrew = existingFirebaseEntity['IsHebrew'];
        this.setLanguage(isHebrew ? 'he' : 'en');
        reservationStore.update((state) => ({
          ...state,
          reservation: existingFirebaseEntity['reservationDetails']
            ? { ...existingFirebaseEntity['reservationDetails'], isHebrew }
            : { isHebrew },
        }));
        return existingFirebaseEntity['qrUrl'];
      } else {
        return null;
      }
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  public async getReservation(
    expirationBoundEntityId: string,
    processId: string,
    token: string
  ) {
    try {
      const res = await lastValueFrom(
        this.clientService.getEarlyCheckInReservation(
          expirationBoundEntityId,
          processId,
          token
        )
      );
      if (!res.success) {
        return false;
      }

      const existingFirebaseEntity = await this.getReservationFromFb(processId);

      if (existingFirebaseEntity && existingFirebaseEntity['driverDetails']) {
        //user has already started a process, this is not his first check in entrance.
        if (existingFirebaseEntity['ProcessStep'] == 'FinalScreen') {
            this.router.navigate([AppRoutePaths.CHECK_IN_COMPLETED], {
            queryParams: { token: expirationBoundEntityId, processId },
          });
        } else {
          this.setDriverDetails(existingFirebaseEntity['driverDetails']);

          reservationStore.update((state) => ({
            ...state,
            reservation: res?.content
              ? {
                  ...res?.content,
                  isHebrew: existingFirebaseEntity['IsHebrew'],
                }
              : { isHebrew: existingFirebaseEntity['IsHebrew'] },
            ui: {
              ...state.ui,
              extraFileName: existingFirebaseEntity['extraFileName'],
              mainFileName: existingFirebaseEntity['mainFileName'],
              passportFileName: existingFirebaseEntity['passportFileName'],
              passportImg: existingFirebaseEntity['passportImg'],
              licenseImg: existingFirebaseEntity['licenseImg'],
              extraDriverLicenseImg:
                existingFirebaseEntity['extraDriverLicenseImg'],
              tafnitDriver: existingFirebaseEntity['tafnitDriver'],
              processId,
              processStep: CheckInProcessSteps.DriverDetails,
              expirationBoundEntityId,
              lang: existingFirebaseEntity['IsHebrew'] ? 'he' : 'en',
            },
          }));
          this.ts.setActiveLang(
            existingFirebaseEntity['IsHebrew'] ? 'he' : 'en'
          );
          //navigate to second page (first is legal id page)
            this.router.navigate([AppRoutePaths.EARLY_CHECK_IN_DRIVER_DETAILS], {
            queryParams: { token: expirationBoundEntityId, processId },
          });
        }

        return true;
      } else {
        let resData = {
          ProcessStep: CheckInProcessSteps.ReservationDetails,
          reservationDetails: res.success ? res?.content : null,
          expirationBoundEntityId,
          processId,
        };
        this.updateCollection(resData, processId);

        const isHebrew = existingFirebaseEntity
          ? existingFirebaseEntity['IsHebrew']
          : false;

        reservationStore.update((state) => ({
          ...state,
          reservation: res?.content
            ? {
                ...res?.content,
                isHebrew,
              }
            : { isHebrew },
          ui: {
            ...state.ui,
            processId,
            processStep: resData.ProcessStep,
            expirationBoundEntityId,
            lang: isHebrew ? 'he' : 'en',
          },
        }));
        this.ts.setActiveLang(isHebrew ? 'he' : 'en');

        this.logger.info(
          `${res?.content?.reservationNumber} - getReservation finished`
        );

        return true;
      }
    } catch (err) {
      return false;
    }
  }

  public async setReservationNumber(reservationNumber: any) {
    reservationStore.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        reservationNumber,
        reservationInputNumber: reservationNumber,
      },
    }));
  }
  public async getConfirmationEmailData() {
    const qrUrl =
      await this.expirationBoundEntityRepository.earlyCheckInEmailUrl(
        this.processId
      );

    await this.updateCollection({ qrUrl }, this.processId);

    //const googleWalletUrl = await this.getGoogleWalletLink();

    //  TODO: MAKE SURE GOOGLE WALLET WORKS AND ADD googleWalletUrl TO DATA
    const storeValue = this.getState();
    const data: IEarlyCheckInDataModel = {
      IsHebrew: storeValue.lang == 'he',
      ReservationNumber: this.reservation?.reservationNumber,
      DriverEmail: storeValue.driverDetails.email,
      DriverLastName: storeValue.driverDetails.lastName,
      DriverFirstName: storeValue.driverDetails.firstName,
      DriverPhone: storeValue.driverDetails.phoneNumber,
      GoogleWalletUrl: '',
      QrUrl: qrUrl,
    };

    console.log('getConfirmationEmailData with data:', { data, qrUrl });

    return data;
  }

  public async setDriverDetails(
    driverData: any,
    updateFb?: boolean,
    updateTafnit?: boolean
  ) {
    this.setIsLoading(true);
    let res;
    reservationStore.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        driverDetails: { ...state.ui.driverDetails, ...driverData },
      },
    }));
    if (updateFb) {
      const currDriverDetails = this.getState()?.driverDetails;
      await this.updateCollection({
        driverDetails: {
          ...currDriverDetails,
        },
      });
    }
    res = { success: true };
    if (updateTafnit) {
      res = await this.setTafnitDriver();
      res = { success: res.success };
    }

    return res;
  }

  public async setTafnitDriver() {
    const driverDetails = this.driverDetails;
    const data: ISetDriverDeatilsModel = {
      reservationNumber: this.reservation?.reservationNumber,
      createAccount: false,
      legalId: driverDetails.legalId,
      firstName: driverDetails.firstName,
      lastName: driverDetails.lastName,
      birthdate: driverDetails.birthDate,
      phone: driverDetails.phoneNumber,
      email: driverDetails.email,
      nationality: driverDetails.nationalityCode,
      countryCode: driverDetails.countryCode,
      stateCode: driverDetails.stateCode,
      city: driverDetails.city,
      address: driverDetails.street,
      houseNumber: driverDetails.houseNumber,
      licenseNumber: driverDetails.licenseNumber,
      licenseDate: driverDetails.licenseDate,
      licenseExp: driverDetails.licenseExpireDate,
    };

    const emailData = await this.getConfirmationEmailData();

    const token = await getAuth().currentUser?.getIdToken();
    const res = await lastValueFrom(
      this.clientService.setDriverDetails(
        { driverData: data, emailData, userId: this.expirationBoundEntityId },
        token
      )
    );

    await this.updateCollection({
      ProcessStep: CheckInProcessSteps.FinalScreen,
    });

    return res;
  }

  public async setLastName(lastName: any) {
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, lastName },
    }));
  }

  public async setIsLoading(isLoading: boolean) {
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, isLoading },
    }));
  }

  public async setProcessStep(processStep: CheckInProcessSteps) {
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, processStep },
    }));
  }

  public async uploadImgToArchive(
    file: File,
    isPassport?: boolean,
    isExtraDriver?: boolean
  ) {
    try {
      const kioskSettings = await this.getKioskSettings();

      let fileTypeForTafnit = 'png';
      if (file.name) {
        const splittedName = file.name.split('.');
        if (fileTypeForTafnit.length > 1) {
          fileTypeForTafnit = splittedName[splittedName.length - 1];
        }
      }

      let imgUrl: string = '';

      if (kioskSettings) {
        const tafniteRequiredData = {
          DocumentName: isPassport
            ? environment.tafniteRequiredData.PassportDocumentName
            : environment.tafniteRequiredData.DocumentName,
          FileType: fileTypeForTafnit,
          ArchiveCode: environment.tafniteRequiredData.ArchiveCode,
          ArchiveKeywords: {
            Hevra: environment.tafniteRequiredData.ArchiveKeywords.Hevrea,
            Sug: isPassport
              ? environment.tafniteRequiredData.ArchiveKeywords.Passport
              : environment.tafniteRequiredData.ArchiveKeywords.FrontLicense,
            Hoze: Number(this.reservation.reservationNumber),
          },
        };
        const metadata: any = {
          tafniteRequiredData: JSON.stringify(tafniteRequiredData),
        };
        const scanType = isPassport ? '_passport' : '_license';

        const path =
          kioskSettings['archiveFolder'] +
          'earlyCheckInDocuments/' +
          this.reservation.reservationNumber +
          '_' +
          this.reservation.firstName +
          scanType;

        imgUrl = await this.filesService.uploadFile(path, file, metadata);
        if (isPassport) {
          this.updateCollection({
            passportImg: imgUrl,
            passportFileName: file.name,
          });
          reservationStore.update((state) => ({
            ...state,
            ui: {
              ...state.ui,
              passportImg: imgUrl,
              passportFileName: file.name,
            },
          }));
        } else if (!isPassport && !isExtraDriver) {
          this.updateCollection({
            licenseImg: imgUrl,
            mainFileName: file.name,
          });
          reservationStore.update((state) => ({
            ...state,
            ui: { ...state.ui, licenseImg: imgUrl, mainFileName: file.name },
          }));
        } else if (!isPassport && isExtraDriver) {
          this.updateCollection({
            extraDriverLicenseImg: imgUrl,
            extraFileName: file.name,
          });
          reservationStore.update((state) => ({
            ...state,
            ui: {
              ...state.ui,
              extraDriverLicenseImg: imgUrl,
              extraFileName: file.name,
            },
          }));
        }
      }
      return imgUrl;
    } catch (err) {
      this.logger.error(
        `${this.reservation?.reservationNumber} - ${this.processId} - uploadImgToArchive failed with error:`,
        { error: getErrorData(err) }
      );
      return null;
    }
  }

  public async resetPassportPhoto() {
    this.updateCollection({ passportImg: null, passportFileName: null });
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, passportImg: undefined, passportFileName: undefined },
    }));
  }

  public async resetMainPhoto() {
    this.updateCollection({ licenseImg: null, mainFileName: null });
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, licenseImg: undefined, mainFileName: undefined },
    }));
  }

  public async resetExtraDriverPhoto() {
    this.updateCollection({ extraDriverLicenseImg: null, extraFileName: null });
    reservationStore.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        extraDriverLicenseImg: undefined,
        extraFileName: undefined,
      },
    }));
  }

  public async setLanguage(lang: any) {
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, lang },
    }));
    this.ts.setActiveLang(lang);
  }

  public async setIsIsraeliLicense(isIsraeli: boolean) {
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, isIsraeliLicense: isIsraeli },
    }));
  }

  public async setDriverId(id: string) {
    this.updateCollection({ driverId: id });
    reservationStore.update((state) => ({
      ...state,
      ui: { ...state.ui, scanDriverId: id },
    }));
  }

  public get reservation() {
    const res = reservationStore.getValue().reservation;
    return res;
  }
  public get processStep() {
    const res = reservationStore.getValue().ui.processStep;
    return res;
  }
  public get isExtraDriver() {
    const res = reservationStore.getValue().reservation.additionalDriver;
    return res;
  }
  public get getUI() {
    const res = reservationStore.getValue().ui;
    return res;
  }
  public get driverDetails() {
    const res = reservationStore.getValue().ui.driverDetails;
    return res;
  }
  public get googleWalletLink() {
    const res = reservationStore.getValue().ui.googleWalletLink;
    return res;
  }
  public get tafnitDriverDetails() {
    const res = reservationStore.getValue().ui.tafnitDriver;
    return res;
  }
  public get processId() {
    const res = reservationStore.getValue().ui.processId;
    return res;
  }
  public get passportImgUrl() {
    const res = reservationStore.getValue().ui.passportImg;
    return res;
  }
  public get licenseImgUrl() {
    const res = reservationStore.getValue().ui.licenseImg;
    return res;
  }
  public get extraFileName() {
    const res = reservationStore.getValue().ui.extraFileName;
    return res;
  }
  public get passportFileName() {
    const res = reservationStore.getValue().ui.passportFileName;
    return res;
  }
  public get mainFileName() {
    const res = reservationStore.getValue().ui.mainFileName;
    return res;
  }
  public get extraDriverLicenseImgUrl() {
    const res = reservationStore.getValue().ui.extraDriverLicenseImg;
    return res;
  }
  public get reservationFromFb() {
    const res = reservationStore.getValue().ui.reservationFromFb;
    return res;
  }
  public get expirationBoundEntityId() {
    const res = reservationStore.getValue().ui.expirationBoundEntityId;
    return res;
  }
}
